<template>
	<template v-if="tourState.isActive && currentStep">
		<!-- Spotlight overlay (tooltip steps only) -->
		<Teleport to="body">
			<svg
				v-if="currentStep.type === TourStepTypeE.Tooltip && spotlightPath"
				class="tour-spotlight-overlay"
			>
				<path
					:d="spotlightPath"
					fill-rule="evenodd"
				/>
			</svg>
		</Teleport>

		<!-- Modal steps -->
		<TourModalStep
			v-if="currentStep.type === TourStepTypeE.Modal"
			v-model="isStepVisible"
			:title="currentStep.title"
			:text="currentStep.text"
			:stepNumber="visibleStepNumber"
			:totalSteps="visibleStepCount"
			@skip="skip"
		>
			<template #actions>
				<v-btn
					v-if="isLastStep"
					variant="flat"
					color="primary"
					@click="finish"
				>
					Finish Tour
				</v-btn>
				<v-btn
					v-else
					variant="flat"
					color="primary"
					@click="next"
				>
					Get Started
					<ArrowRight16 />
				</v-btn>
			</template>
		</TourModalStep>

		<!-- Tooltip steps -->
		<TourTooltipStep
			v-if="currentStep.type === TourStepTypeE.Tooltip"
			v-model="isStepVisible"
			:targetSelector="currentStep.targetSelector"
			:title="currentStep.title"
			:tooltipLocation="resolvedTooltipLocation"
			:zIndex="10000"
			:stepNumber="visibleStepNumber"
			:totalSteps="visibleStepCount"
			@skip="skip"
		>
			<div class="sgnl-flex sgnl-flex-column sgnl-gap-8">
				<div
					class="sgnl-paragraph-m-regular"
					v-html="currentStep.text"
				/>
				<AlertMessage
					v-if="currentStep.interactive && tourState.taskFailed"
					type="error"
				>
					The task failed. Please click Run to try again.
				</AlertMessage>
			</div>

			<template #actions>
				<v-btn
					variant="flat"
					color="primary"
					:disabled="isWaitingStep"
					@click="next"
				>
					Next
					<ArrowRight16 />
				</v-btn>
			</template>
		</TourTooltipStep>
	</template>

	<!-- Leave confirmation dialog -->
	<TourLeaveConfirmDialog
		v-model:show-dialog="tourState.showLeaveConfirm"
		@stay="cancelLeave"
		@leave="proceedLeave"
	/>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import { useTour, TourStepTypeE, TourTooltipStep as TourTooltipStepType } from "@/composables/useTour";
import TourModalStep from "@/components/GuidedTour/TourModalStep.vue";
import TourTooltipStep from "@/components/GuidedTour/TourTooltipStep.vue";
import TourLeaveConfirmDialog from "@/components/GuidedTour/TourLeaveConfirmDialog.vue";
import AlertMessage from "@/components/Common/AlertMessage.vue";

const SPOTLIGHT_PADDING = 8;
const MIN_TOOLTIP_SPACE = 250;
const OPPOSITE_DIRECTION: Record<string, string> = {
	top: "bottom",
	bottom: "top",
	start: "end",
	end: "start",
};
const CROSS_AXIS: Record<string, string[]> = {
	top: ["start", "end"],
	bottom: ["start", "end"],
	start: ["top", "bottom"],
	end: ["top", "bottom"],
};

export default defineComponent({
	name: "GuidedTour",
	components: { ArrowRight16, TourModalStep, TourTooltipStep, TourLeaveConfirmDialog, AlertMessage },
	setup() {
		const {
			tourState,
			currentStep,
			visibleStepCount,
			visibleStepNumber,
			next,
			skip,
			finish,
			cancelLeave,
			proceedLeave,
		} = useTour();
		return {
			tourState,
			currentStep,
			visibleStepCount,
			visibleStepNumber,
			next,
			skip,
			finish,
			cancelLeave,
			proceedLeave,
			TourStepTypeE,
		};
	},
	data() {
		return {
			spotlightPath: "" as string,
			forceMenuReposition: false,
			rafId: 0,
			resolvedTooltipLocation: "bottom" as string,
		};
	},
	computed: {
		isStepVisible: {
			get(): boolean {
				return this.tourState.isActive && !this.forceMenuReposition;
			},
			set() {
				/* Visibility is controlled by tourState, not by Vuetify */
			},
		},
		isLastStep(): boolean {
			return this.tourState.currentStepIndex === this.tourState.steps.length - 1;
		},
		isWaitingStep(): boolean {
			const step = this.currentStep;
			if (!step || step.type !== TourStepTypeE.Tooltip) return false;
			const ts = step as TourTooltipStepType;
			return !!(
				ts.interactive ||
				ts.waitForRoute ||
				ts.waitForClickSelector ||
				(ts.waitForTaskCompletion && this.tourState.waitingForTaskCompletion)
			);
		},
	},
	watch: {
		"tourState.currentStepIndex"() {
			this.forceMenuReposition = true;
			this.$nextTick(async () => {
				const step = this.currentStep;
				if (step?.beforeShow) {
					await step.beforeShow();
					await this.$nextTick();
				}

				let target: Element | null = null;
				if (step?.type === TourStepTypeE.Tooltip) {
					target = document.querySelector((step as TourTooltipStepType).targetSelector);
					if (target) {
						await this.scrollTargetIntoView(target);
					}
				}

				this.updateTooltipLocation(target);
				this.forceMenuReposition = false;
				await this.$nextTick();

				this.updateSpotlight();
			});
		},
		"tourState.isActive"(active: boolean) {
			if (active) {
				this.startSpotlightTracking();
			} else {
				this.stopSpotlightTracking();
			}
		},
	},
	mounted() {
		if (this.tourState.isActive) {
			this.startSpotlightTracking();
		}
	},
	beforeUnmount() {
		this.stopSpotlightTracking();
	},
	methods: {
		/**
		 * Scroll the target element into view with enough room for the tooltip.
		 * On smaller viewports, uses "center" to ensure the element isn't clipped
		 * by the tooltip or off-screen edges. On desktop, "nearest" suffices.
		 */
		updateTooltipLocation(target: Element | null) {
			const step = this.currentStep;
			if (!step || step.type !== TourStepTypeE.Tooltip) {
				this.resolvedTooltipLocation = "bottom";
				return;
			}
			const location = (step as TourTooltipStepType).tooltipLocation || "bottom";
			if (window.innerWidth < 960 && (location === "end" || location === "start")) {
				this.resolvedTooltipLocation = "bottom";
				return;
			}

			if (!target) {
				this.resolvedTooltipLocation = location;
				return;
			}

			const rect = target.getBoundingClientRect();
			const spaceMap: Record<string, number> = {
				top: rect.top,
				bottom: window.innerHeight - rect.bottom,
				start: rect.left,
				end: window.innerWidth - rect.right,
			};

			if (spaceMap[location] >= MIN_TOOLTIP_SPACE) {
				this.resolvedTooltipLocation = location;
				return;
			}

			/*
			 *	When the preferred direction doesn't fit, try the opposite
			 *	on the same axis first (e.g. bottom→top) so the tooltip
			 *	stays visually aligned with the target. Only cross axes
			 *	as a last resort.
			 */
			const candidates = [OPPOSITE_DIRECTION[location], ...CROSS_AXIS[location]];
			for (const dir of candidates) {
				if (spaceMap[dir] >= MIN_TOOLTIP_SPACE) {
					this.resolvedTooltipLocation = dir;
					return;
				}
			}

			this.resolvedTooltipLocation = location;
		},
		async scrollTargetIntoView(target: Element) {
			const isMobile = window.innerWidth < 960;
			target.scrollIntoView({
				behavior: "smooth",
				block: isMobile ? "center" : "nearest",
			});
			/* Wait for smooth scroll to settle — mobile needs more time */
			await new Promise((r) => setTimeout(r, isMobile ? 500 : 300));
		},
		updateSpotlight() {
			const step = this.currentStep;
			if (!step || step.type !== TourStepTypeE.Tooltip) {
				this.spotlightPath = "";
				return;
			}

			const tooltipStep = step as TourTooltipStepType;
			const target = document.querySelector(tooltipStep.targetSelector);
			if (!target) {
				this.spotlightPath = "";
				return;
			}

			const rect = target.getBoundingClientRect();
			const pad = SPOTLIGHT_PADDING;
			const cs = window.getComputedStyle(target);
			const radius = parseFloat(cs.borderRadius) || 0;
			const r = Math.min(radius + pad, (rect.width + pad * 2) / 2, (rect.height + pad * 2) / 2);

			const x = rect.left - pad;
			const y = rect.top - pad;
			const w = rect.width + pad * 2;
			const h = rect.height + pad * 2;
			const vw = window.innerWidth;
			const vh = window.innerHeight;

			const outer = `M0,0 H${vw} V${vh} H0 Z`;
			const cutout = [
				`M${x + r},${y}`,
				`H${x + w - r}`,
				`A${r},${r} 0 0 1 ${x + w},${y + r}`,
				`V${y + h - r}`,
				`A${r},${r} 0 0 1 ${x + w - r},${y + h}`,
				`H${x + r}`,
				`A${r},${r} 0 0 1 ${x},${y + h - r}`,
				`V${y + r}`,
				`A${r},${r} 0 0 1 ${x + r},${y}`,
				`Z`,
			].join(" ");

			this.spotlightPath = `${outer} ${cutout}`;
		},
		throttledUpdateSpotlight() {
			if (!this.rafId) {
				this.rafId = requestAnimationFrame(() => {
					this.rafId = 0;
					this.updateSpotlight();
				});
			}
		},
		startSpotlightTracking() {
			this.updateSpotlight();
			window.addEventListener("resize", this.throttledUpdateSpotlight);
			window.addEventListener("scroll", this.throttledUpdateSpotlight, true);
		},
		stopSpotlightTracking() {
			window.removeEventListener("resize", this.throttledUpdateSpotlight);
			window.removeEventListener("scroll", this.throttledUpdateSpotlight, true);
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = 0;
			}
			this.spotlightPath = "";
		},
	},
});
</script>

<style lang="scss">
.tour-spotlight-overlay {
	position: fixed;
	inset: 0;
	width: 100%;
	height: 100%;
	z-index: 9999;
	pointer-events: none;

	path {
		fill: var(--sgnl-background-overlay);
		pointer-events: all;
	}
}

@media (min-width: 960px) {
	.tour-mobile-only {
		display: none;
	}
}
@media (max-width: 959px) {
	.tour-desktop-only {
		display: none;
	}
}
</style>
